/**
 * Hover tooltip (desktop) and tap-to-inspect content (touch): the same
 * builder feeds both, ported from the prototype's updateTip().
 */
import { GREATER_SLATES, SLATES, TIER_COLORS } from '../data/balance.js';
import { Game } from '../core/game.js';
import { dist } from '../core/dist.js';
import type { Creep } from '../core/types.js';
import type { ViewState } from '../render/viewState.js';
import { byId, clear, el } from './dom.js';
import { descFor, swatchFor } from './descriptions.js';

function creepContent(game: Game, best: Creep): HTMLElement[] {
  const hpFrac = Math.max(0, best.hp / best.maxhp);
  const parts: HTMLElement[] = [
    el('div', {
      className: 'tname',
      text: best.boss ? 'Boss' : 'Creep',
      style: { color: best.boss ? '#ff6b7a' : '#e0958f' },
    }),
    el('div', { className: 'hpbar' }, [
      el('div', {
        style: {
          width: `${Math.round(hpFrac * 100)}%`,
          background: hpFrac > 0.5 ? '#5fd068' : hpFrac > 0.25 ? '#ff9e3d' : '#ff6b7a',
        },
      }),
    ]),
    el('span', {
      className: 'stat',
      text: `♥ ${Math.ceil(best.hp)}/${best.maxhp}`,
      style: { color: '#ff8c8c' },
    }),
    el('span', {
      className: 'stat',
      text: `🛡 ${Game.effArmor(best)}${best.shred ? ` (-${best.shred})` : ''}`,
    }),
    el('span', { className: 'stat', text: `➤ ${best.speed}` }),
  ];
  const traits = [best.boss && 'BOSS', best.flying && 'FLYING'].filter(
    (t): t is string => typeof t === 'string',
  );
  if (traits.length) {
    parts.push(
      el(
        'div',
        { style: { marginTop: '4px' } },
        traits.map((t) => {
          const col = t === 'BOSS' ? '#ff6b7a' : '#d78cff';
          return el('span', {
            className: 'chip',
            text: t,
            style: {
              background: `${col}22`,
              color: col,
              border: '1px solid currentColor',
              marginLeft: '0',
              marginRight: '4px',
            },
          });
        }),
      ),
    );
  }
  if (best.poisonDps && game.gt < best.poisonUntil) {
    parts.push(
      el('div', {
        text: `☠ Poisoned · ${best.poisonDps}/s`,
        style: { color: '#2ecc71', fontSize: '11px', marginTop: '2px' },
      }),
    );
  }
  return parts;
}

/** Content for whatever is under (mx,my)/hoverCell, or null for nothing. */
export function buildTipContent(game: Game, view: ViewState): HTMLElement[] | null {
  if (view.mouseCanvas) {
    let best: Creep | null = null;
    let bestD = 14;
    for (const c of game.creeps) {
      if (c.dead) continue;
      const d = dist(c.px - view.mouseCanvas.mx, c.py - view.mouseCanvas.my);
      if (d < bestD) {
        bestD = d;
        best = c;
      }
    }
    if (best) return creepContent(game, best);
  }
  if (view.hoverCell) {
    const g = game.grid[view.hoverCell.y]?.[view.hoverCell.x];
    if (g && g.kind === 'gem') {
      const st = game.gemStats(g);
      const nameCol = g.special !== undefined ? st.color : TIER_COLORS[g.tier]!;
      const parts: HTMLElement[] = [
        el('div', { className: 'tname', style: { color: nameCol } }, [swatchFor(g), st.name]),
        el('span', { className: 'stat', text: `⚔ ${st.dmg}` }),
        el('span', { className: 'stat', text: `◎ ${st.range.toFixed(1)}` }),
        el('span', { className: 'stat', text: `⚡ ${(1 / st.cd).toFixed(1)}/s` }),
        el('div', { className: 'ability', text: descFor(g), style: { color: st.color } }),
      ];
      if (g.isNew && game.phase !== 'wave' && g.special === undefined) {
        parts.push(
          el('div', {
            className: 'tdesc',
            text: 'Click to pick it back up',
            style: { marginTop: '3px' },
          }),
        );
      } else if (!g.isNew && game.phase === 'build' && !game.lifted) {
        parts.push(
          el('div', { className: 'tdesc', text: 'Click to move', style: { marginTop: '3px' } }),
        );
      }
      return parts;
    }
    if (g && g.kind === 'slate') {
      const def = SLATES[g.key];
      const greater = GREATER_SLATES.find((gs) => gs.pair.includes(g.key));
      const parts = [
        el('div', { className: 'tname', text: def.name, style: { color: def.color } }),
        el('span', { className: 'stat', text: `◎ ${def.radius}` }),
        el('div', { className: 'ability', text: def.desc, style: { color: def.color } }),
      ];
      if (greater) {
        const partner = greater.pair[0] === g.key ? greater.pair[1] : greater.pair[0];
        parts.push(
          el('div', {
            className: 'tdesc',
            text: `+ ${SLATES[partner].name} → ${SLATES[greater.key].name} (tap to combine)`,
            style: { marginTop: '3px', color: SLATES[greater.key].color },
          }),
        );
      }
      parts.push(
        el('div', {
          className: 'tdesc',
          text: 'Socketed into the rock · cannot be moved.',
          style: { marginTop: '3px' },
        }),
      );
      return parts;
    }
    if (g) {
      const parts = [
        el('div', { className: 'tname', text: 'Rock' }),
        el('div', { className: 'tdesc', text: 'Maze wall · reroutes the creeps.' }),
      ];
      if (game.phase === 'build' && !game.lifted) {
        parts.push(
          el('div', { className: 'tdesc', text: 'Click to move', style: { marginTop: '3px' } }),
        );
      }
      return parts;
    }
  }
  return null;
}

export function updateTip(game: Game, view: ViewState, clientX: number, clientY: number): void {
  const tip = byId('tip');
  const content = buildTipContent(game, view);
  if (content) {
    clear(tip);
    tip.append(...content);
    tip.style.display = 'block';
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    tip.style.left = `${Math.min(clientX + 16, window.innerWidth - tw - 8)}px`;
    tip.style.top = `${Math.min(clientY + 16, window.innerHeight - th - 8)}px`;
  } else {
    tip.style.display = 'none';
  }
}

export function hideTip(): void {
  byId('tip').style.display = 'none';
}
